/**
 * WUI-365: `GET /platform/download` — multipart meta + file, staging по documentSessionId.
 *
 * @see dev/WUI-365.md
 */
import api from '@/common/constants/api'
import { axios as axiosInstance } from '@/common/plugins/axios'
import axios from 'axios'
import type { FileItem } from '@/common/types/fileItem'
import { isMiddleErrorResponse } from '@/common/utils/middleErrorResponse'
import {
  appendAttachmentPlatformReadQueryFields,
  clearAttachmentDocumentSessionIdFields,
  ensureAttachmentClientRowKey,
  getScalarFromFields,
  mergePlatformDownloadMetaIntoRow,
  parsePlatformDownloadMeta,
  rowAttachmentKey,
  type PlatformDownloadMeta,
} from '@/common/utils/fileAttachmentsUniData'
import {
  getDsAttachmentStore,
  resolveAttachmentDocumentSessionId,
  upsertDsStoreFromOfficeStagingMeta,
} from '@/common/utils/dsAttachmentSnapshot'

export type PlatformDownloadQueryParams = Record<string, string | number>

export type PlatformDownloadResult = {
  blob: Blob
  meta: PlatformDownloadMeta
}

const inflightDownloads = new Map<string, Promise<PlatformDownloadResult | null>>()

export function buildPlatformDownloadDedupeKey(options: {
  elementId: number | string
  sessionId: string
  item: FileItem
  params: PlatformDownloadQueryParams
}): string {
  const docId = String(options.params.documentSessionId ?? '').trim()
  if (docId) return `ds:${docId}`

  const fields = (options.item.fields ?? {}) as Record<string, unknown>
  const rowKey = rowAttachmentKey(fields) || `client:${ensureAttachmentClientRowKey(options.item)}`
  return `dl:${options.params.elementId}:${options.params.sessionId}:${rowKey}`
}

export function buildPlatformDownloadQueryParams(options: {
  elementId: number | string
  sessionId: string
  type?: string | null
  item: FileItem
  /** Игнорировать documentSessionId в row (retry после протухшей сессии). */
  forcePlatformRead?: boolean
}): PlatformDownloadQueryParams | null {
  const elementId = Number(options.elementId)
  if (!Number.isFinite(elementId) || elementId <= 0) return null

  const sessionId = String(options.sessionId ?? '').trim()
  if (!sessionId) return null

  const base: PlatformDownloadQueryParams = {
    type: String(options.type ?? 'WF'),
    sessionId,
    elementId,
  }

  if (!options.forcePlatformRead) {
    const documentSessionId = resolveAttachmentDocumentSessionId(options.item, elementId)
    if (documentSessionId) {
      return { ...base, documentSessionId }
    }
  }

  const fields = (options.item.fields ?? {}) as Record<string, unknown>
  const code = getScalarFromFields(fields, 'code', '_id', 'CODE', 'Code')
  const codeNum = Number(code)
  if (!Number.isFinite(codeNum) || codeNum <= 0) return null

  const params: PlatformDownloadQueryParams = { ...base }
  appendAttachmentPlatformReadQueryFields(params, fields)
  if (!params.fileName) {
    params.fileName = 'unknown'
  }
  return params
}

/** Merge meta в row + DS-снимок (полный prepare bundle → Р7 без повторного prepare). */
export function applyPlatformDownloadMeta(
  item: FileItem,
  elementId: number | string,
  meta: PlatformDownloadMeta,
): FileItem {
  const updated = mergePlatformDownloadMetaIntoRow(item, meta)
  const eid = Number(elementId)
  if (Number.isFinite(eid) && eid > 0) {
    upsertDsStoreFromOfficeStagingMeta(updated, eid, meta)
  }
  return updated
}

function parseBoundary(contentType: string | null | undefined): string {
  if (!contentType) return 'boundary'
  const match = /boundary=(?:"([^"]+)"|([^\s;]+))/i.exec(contentType)
  return match?.[1] ?? match?.[2] ?? 'boundary'
}

/** Разбор `multipart/mixed` ответа middleware (part `unidata` + octet-stream). */
export function parsePlatformDownloadMultipart(
  body: ArrayBuffer,
  contentType?: string | null,
): { metaJson: unknown; blob: Blob } | null {
  const bytes = new Uint8Array(body)
  const boundary = parseBoundary(contentType)
  const partDelimiter = encodeAscii(`\r\n--${boundary}\r\n`)
  const closingDelimiter = encodeAscii(`\r\n--${boundary}--`)

  const pos = findOpeningBoundary(bytes, boundary)
  if (pos < 0) return null

  const part1HeaderEnd = indexOfDoubleCrlf(bytes, pos)
  if (part1HeaderEnd < 0) return null

  const part2Start = indexOfSubarray(bytes, partDelimiter, part1HeaderEnd + 4)
  if (part2Start < 0) return null

  const metaBytes = trimTrailingCrlf(bytes.subarray(part1HeaderEnd + 4, part2Start))
  let metaJson: unknown
  try {
    metaJson = JSON.parse(decodeUtf8(metaBytes))
  } catch {
    return null
  }

  const pos2 = part2Start + partDelimiter.length
  const part2HeaderEnd = indexOfDoubleCrlf(bytes, pos2)
  if (part2HeaderEnd < 0) return null

  const fileEnd = indexOfSubarray(bytes, closingDelimiter, part2HeaderEnd + 4)
  const rawFile =
    fileEnd >= 0
      ? bytes.subarray(part2HeaderEnd + 4, fileEnd)
      : bytes.subarray(part2HeaderEnd + 4)
  const fileBytes = trimTrailingCrlf(rawFile)
  const payload = new Uint8Array(fileBytes.byteLength)
  payload.set(fileBytes)

  return {
    metaJson,
    blob: new Blob([payload], { type: 'application/octet-stream' }),
  }
}

function findOpeningBoundary(bytes: Uint8Array, boundary: string): number {
  const opening = encodeAscii(`--${boundary}`)
  const idx = indexOfSubarray(bytes, opening)
  if (idx < 0) return -1
  const pos = idx + opening.length
  if (bytes[pos] === 0x0d && bytes[pos + 1] === 0x0a) return pos + 2
  if (bytes[pos] === 0x0a) return pos + 1
  return pos
}

function encodeAscii(value: string): Uint8Array {
  return new TextEncoder().encode(value)
}

function decodeUtf8(bytes: Uint8Array): string {
  return new TextDecoder('utf-8').decode(bytes)
}

function indexOfSubarray(haystack: Uint8Array, needle: Uint8Array, from = 0): number {
  if (needle.length === 0) return from
  outer: for (let i = from; i <= haystack.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) continue outer
    }
    return i
  }
  return -1
}

function indexOfDoubleCrlf(bytes: Uint8Array, from: number): number {
  for (let i = from; i < bytes.length - 3; i++) {
    if (bytes[i] === 0x0d && bytes[i + 1] === 0x0a && bytes[i + 2] === 0x0d && bytes[i + 3] === 0x0a) {
      return i
    }
  }
  return -1
}

function trimTrailingCrlf(bytes: Uint8Array): Uint8Array {
  let end = bytes.length
  while (end > 0 && (bytes[end - 1] === 0x0a || bytes[end - 1] === 0x0d)) {
    end--
  }
  return bytes.subarray(0, end)
}

function parseMiddleErrorFromArrayBuffer(data: ArrayBuffer): { code: number; message: string } | null {
  try {
    const text = decodeUtf8(new Uint8Array(data))
    const json = JSON.parse(text) as unknown
    if (isMiddleErrorResponse(json)) {
      return { code: Number(json.code), message: json.message }
    }
  } catch {
    // not JSON error
  }
  return null
}

function isStaleDocumentSessionError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  const data = error.response?.data
  if (data instanceof ArrayBuffer) {
    const parsed = parseMiddleErrorFromArrayBuffer(data)
    if (parsed?.code === 4007 && /session=.*does not exist/i.test(parsed.message)) {
      return true
    }
  }
  if (data && typeof data === 'object' && isMiddleErrorResponse(data)) {
    return data.code === 4007 && /does not exist/i.test(data.message)
  }
  return false
}

async function requestPlatformDownloadOnce(
  params: PlatformDownloadQueryParams,
): Promise<PlatformDownloadResult> {
  const response = await axiosInstance({
    ...api.platform.getFile,
    params,
    responseType: 'arraybuffer',
  })

  const contentType =
    typeof response.headers?.['content-type'] === 'string'
      ? response.headers['content-type']
      : null

  const parsed = parsePlatformDownloadMultipart(response.data as ArrayBuffer, contentType)
  if (!parsed) {
    throw new Error('[platform/download] Unable to parse multipart response')
  }

  const meta = parsePlatformDownloadMeta(parsed.metaJson)
  if (!meta) {
    throw new Error('[platform/download] Meta part does not contain documentSessionId')
  }

  return { blob: parsed.blob, meta }
}

/**
 * Скачивание вложения с middleware: multipart meta + blob.
 * Дедупликация параллельных запросов на одну строку / documentSessionId.
 */
export async function fetchPlatformAttachmentDownload(options: {
  elementId: number | string
  sessionId: string
  type?: string | null
  item: FileItem
}): Promise<PlatformDownloadResult | null> {
  const params = buildPlatformDownloadQueryParams(options)
  if (!params) return null

  const dedupeKey = buildPlatformDownloadDedupeKey({ ...options, params })
  const existing = inflightDownloads.get(dedupeKey)
  if (existing) return existing

  const task = (async (): Promise<PlatformDownloadResult | null> => {
    try {
      return await requestPlatformDownloadOnce(params)
    } catch (error) {
      const elementId = Number(params.elementId)
      if (
        isStaleDocumentSessionError(error)
        && resolveAttachmentDocumentSessionId(options.item, elementId)
      ) {
        if (Number.isFinite(elementId) && elementId > 0) {
          try {
            getDsAttachmentStore(elementId).clearSnapshot(options.item)
          } catch {
            // ignore invalid elementId
          }
        }
        const clearedItem = clearAttachmentDocumentSessionIdFields(options.item)
        const retryParams = buildPlatformDownloadQueryParams({
          ...options,
          item: clearedItem,
          forcePlatformRead: true,
        })
        if (!retryParams) return null
        return await requestPlatformDownloadOnce(retryParams)
      }
      throw error
    }
  })()

  inflightDownloads.set(dedupeKey, task)
  try {
    return await task
  } finally {
    inflightDownloads.delete(dedupeKey)
  }
}
